/**
 * CompactDropdown Component
 *
 * Generic searchable dropdown with autocomplete support.
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronDown, Search, Check } from 'lucide-react';
import clsx from 'clsx';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import { useClickOutside } from '@/hooks/useClickOutside';

export interface CompactDropdownOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  hint?: string;
}

export interface CompactDropdownProps {
  value: string | null;
  onChange: (value: string | null) => void;
  options: CompactDropdownOption[];
  placeholder?: string;
  icon?: React.ReactNode;
  size?: 'sm' | 'md';
  disabled?: boolean;
  searchable?: boolean;
  allowClear?: boolean;
  className?: string;
}

export function CompactDropdown({
  value,
  onChange,
  options,
  placeholder = 'Seleziona...',
  icon,
  size = 'md',
  disabled = false,
  searchable = true,
  allowClear = true,
  className,
}: CompactDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownStyle = useDropdownPosition(isOpen, triggerRef, {
    offset: 6,
    minWidth: 200,
    matchTriggerWidth: true,
  });

  const selectedOption = options.find((opt) => opt.value === value);

  const filteredOptions = useMemo(() => {
    if (!query) return options;
    const lowerQuery = query.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(lowerQuery) || opt.value.toLowerCase().includes(lowerQuery)
    );
  }, [options, query]);

  useClickOutside(
    [dropdownRef, triggerRef],
    () => {
      setIsOpen(false);
      setQuery('');
    },
    isOpen
  );

  useEffect(() => {
    if (isOpen && searchable && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue === value ? null : optionValue);
    setIsOpen(false);
    setQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setIsOpen(false);
    setQuery('');
  };

  return (
    <>
      <button
        ref={triggerRef}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setIsOpen(!isOpen);
        }}
        disabled={disabled}
        className={clsx(
          'flex items-center gap-1.5 rounded-md transition-all bg-surface-tertiary',
          size === 'sm' ? 'px-1.5 h-6 text-[10px]' : 'px-2 h-7 text-[11px]',
          !disabled && 'hover:bg-surface-hover cursor-pointer',
          disabled && 'cursor-default opacity-70',
          className
        )}
      >
        {icon && <span className="shrink-0">{icon}</span>}
        <span
          className={clsx(
            'truncate',
            selectedOption ? 'text-text-secondary' : 'text-text-quaternary'
          )}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        {!disabled && (
          <>
            {allowClear && value && (
              <X
                className="w-3 h-3 text-text-quaternary hover:text-text-secondary shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear(e);
                }}
              />
            )}
            <ChevronDown
              className={clsx(
                'w-3 h-3 text-text-quaternary shrink-0 transition-transform',
                isOpen && 'rotate-180'
              )}
            />
          </>
        )}
      </button>

      {isOpen &&
        dropdownStyle &&
        createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="rounded-lg shadow-2xl animate-fade-in border border-border-primary/70 bg-surface-primary z-50"
            onClick={(e) => e.stopPropagation()}
          >
            {searchable && (
              <div className="flex items-center gap-2 px-3 py-2 border-b border-border-primary/50">
                <Search className="w-3.5 h-3.5 text-text-quaternary shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Cerca..."
                  className="flex-1 bg-transparent text-[11px] text-text-primary placeholder:text-text-quaternary focus:outline-none"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
            <div className="max-h-64 overflow-y-auto py-1">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-[11px] text-text-quaternary text-center">
                  Nessun risultato
                </div>
              ) : (
                filteredOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleSelect(opt.value)}
                    className={clsx(
                      'w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11px] hover:bg-surface-hover transition-colors',
                      opt.value === value && 'bg-surface-hover/80 text-text-primary'
                    )}
                  >
                    {opt.icon && <span className="text-text-tertiary shrink-0">{opt.icon}</span>}
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-text-primary truncate">{opt.label}</span>
                      {opt.hint && (
                        <span className="text-[10px] text-text-quaternary">{opt.hint}</span>
                      )}
                    </div>
                    {opt.value === value && (
                      <Check className="w-3.5 h-3.5 text-accent-violet shrink-0" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
